import React from 'react';
import { Clock, CheckCircle, BookOpen, BarChart3 } from 'lucide-react';
import {
  Cpu, Brain, Sparkles, Shield, Boxes, GitMerge, Terminal, Server,
  type LucideIcon,
} from 'lucide-react';
import { useTrainingTranslation } from '../hooks/useTrainingTranslation';
import Button from '../../common/Button';
import type { TrainingCourse } from '../types';

const iconMap: Record<string, LucideIcon> = {
  Cpu,
  Brain,
  Sparkles,
  Shield,
  Boxes,
  GitMerge,
  Terminal,
  Server,
};

const levelStyles: Record<string, string> = {
  Beginner: 'bg-green-50 text-green-700 border-green-200',
  'Beginner to Intermediate': 'bg-green-50 text-green-700 border-green-200',
  Intermediate: 'bg-blue-50 text-blue-700 border-blue-200',
  'Intermediate to Advanced': 'bg-orange-50 text-orange-700 border-orange-200',
  Advanced: 'bg-red-50 text-red-700 border-red-200',
};

interface CourseCardProps {
  courseKey: string;
}

const CourseCard: React.FC<CourseCardProps> = ({ courseKey }) => {
  const { getTranslation, t } = useTrainingTranslation();
  const translation = getTranslation();
  const course: TrainingCourse | undefined = translation.courses[courseKey];

  if (!course || !course.title || !course.topics) return null;

  const Icon = iconMap[course.icon] ?? Cpu;
  const levelClass = levelStyles[course.level] ?? levelStyles.Intermediate;

  return (
    <div id={courseKey} className="scroll-mt-40 bg-white rounded-2xl shadow-lg overflow-hidden h-full flex flex-col border border-gray-100 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
      <div className="bg-gradient-to-r from-gray-900 to-blue-900 p-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl -mr-8 -mt-8" />
        <div className="flex items-center justify-between relative">
          <div className="flex items-center">
            <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
              <Icon className="h-6 w-6 text-blue-300" />
            </div>
            <div className="ml-4">
              <h3 className="text-xl font-bold text-white">{course.title}</h3>
              <div className="flex items-center mt-2 gap-3">
                <span className="flex items-center text-blue-200 text-sm">
                  <Clock className="h-4 w-4 mr-1.5" />
                  {course.duration}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-4 relative">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${levelClass}`}>
            <BarChart3 className="h-3 w-3" />
            {course.level}
          </span>
        </div>
      </div>

      <div className="p-6 flex-grow flex flex-col">
        <p className="text-gray-600">{course.description}</p>

        {course.prerequisites && course.prerequisites.length > 0 && (
          <div className="mt-6">
            <h4 className="font-semibold text-gray-900">{t('labels.prerequisites')}</h4>
            <ul className="mt-2 list-disc list-inside text-gray-500 text-sm space-y-1">
              {course.prerequisites.map((prereq, index) => (
                <li key={index}>{prereq}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-6 flex-grow">
          <h4 className="font-semibold text-gray-900 flex items-center">
            <BookOpen className="h-5 w-5 mr-2 text-blue-600" />
            {t('labels.topics')}
          </h4>
          <ul className="mt-3 space-y-2">
            {course.topics.map((topic, index) => (
              <li key={index} className="flex items-start text-gray-600 text-sm">
                <CheckCircle className="h-4 w-4 mr-2 text-blue-500 flex-shrink-0 mt-0.5" />
                <span>{topic}</span>
              </li>
            ))}
          </ul>
        </div>

        {course.includes && course.includes.length > 0 && (
          <div className="mt-6 bg-gray-50 rounded-xl p-4 border border-gray-100">
            <h4 className="font-semibold text-gray-900 text-sm">{t('labels.includes')}</h4>
            <ul className="mt-2 space-y-1.5">
              {course.includes.map((item, index) => (
                <li key={index} className="flex items-start text-gray-600 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 mr-2 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-8">
          <Button
            variant="primary"
            className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
            onClick={() => document.getElementById('training-request')?.scrollIntoView({ behavior: 'smooth' })}
          >
            {translation.request.cta}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CourseCard;
